import type { PortalProducts } from '@features/products/types';
import { isProductHeld } from '@features/products/productGates';
import { useGetMissingRefBadgeCountQuery } from '../queries/getMissingRefBadgeCount.query.generated';

/** A minute is fresh enough for a nav badge and light on Hasura for every open portal tab. */
const POLL_MS = 60_000;

interface MissingRefBadgeCountInput {
  /** The rooftop's `reference_num_report_enabled`. No report, no query. */
  enabled: boolean;
  /** The working rooftop, falling back to the user's customer, as the report does. */
  customerId: number | null | undefined;
  products?: PortalProducts;
}

/**
 * How many finished transactions are missing a reference number, for the
 * sidebar badge: completed moves (the report's "Completed" metric) plus
 * completed mobile service stops. These are what block reimbursement. It
 * counts all time, not the report's date window, so changing the report's
 * date filter doesn't change the badge. Zero while loading or on error, which
 * shows no badge rather than a wrong one.
 */
export function useMissingRefBadgeCount({ enabled, customerId, products }: MissingRefBadgeCountInput): number {
  const withStops = isProductHeld(products?.entries?.['mobile-service']);

  const { data } = useGetMissingRefBadgeCountQuery({
    variables: { customerId: customerId ?? 0, withStops },
    skip: !enabled || !customerId,
    pollInterval: POLL_MS,
    fetchPolicy: 'cache-and-network',
  });

  if (!enabled || !customerId) return 0;
  const moves = data?.completedMovesMissingRef?.aggregate?.count ?? 0;
  const stops = withStops ? (data?.completedStopsMissingRo?.aggregate?.count ?? 0) : 0;
  return moves + stops;
}
